import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Button, Typography, Paper, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, MenuItem, Select, InputLabel, FormControl,
  Chip, Stack, Alert, Snackbar, useTheme, useMediaQuery
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import GoogleBookSearch from '../components/books/GoogleBookSearch';
import IsbnBookDialog from '../components/books/IsbnBookDialog';
import BatchIsbnDialog from '../components/books/BatchIsbnDialog';
import BooksList from '../components/books/BooksList';
import * as Yup from 'yup';
import { booksAPI, categoriesAPI } from '../services/api';
import { Formik, Form, Field } from 'formik';

const BookSchema = Yup.object().shape({
  title: Yup.string().required('Title is required'),
  author: Yup.string().required('Author is required'),
  durationOptions: Yup.array().of(Yup.number()).min(1, 'At least one duration option is required'),
  cover: Yup.string().url('Must be a valid URL').required('Cover URL is required'),
  status: Yup.string().required('Status is required'),
  audioUrls: Yup.object().required('Audio URLs are required'),
  categoryIds: Yup.array().of(Yup.string()).min(1, 'At least one category is required')
});

export default function BooksManagement() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams, setSearchParams] = useSearchParams();
  const [books, setBooks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingBook, setEditingBook] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isbnDialogOpen, setIsbnDialogOpen] = useState(false);
  const [batchIsbnDialogOpen, setBatchIsbnDialogOpen] = useState(false);
  const [googleBookSearchOpen, setGoogleBookSearchOpen] = useState(false);
  
  const [statusFilter, setStatusFilter] = useState(() => {
    const status = searchParams.get('status') || '';
    return status;
  });
  
  const [page, setPage] = useState(() => {
    const pageParam = parseInt(searchParams.get('page'));
    return pageParam && !isNaN(pageParam) ? pageParam : 1;
  });
  const [limit, setLimit] = useState(() => {
    const limitParam = parseInt(searchParams.get('limit'));
    return limitParam && !isNaN(limitParam) ? limitParam : 20;
  });
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    params.set('page', page.toString());
    params.set('limit', limit.toString());
    
    if (statusFilter) {
      params.set('status', statusFilter);
    } else {
      params.delete('status');
    }
    
    setSearchParams(params);
  }, [page, limit, statusFilter, setSearchParams, searchParams]);

  const fetchBooks = async (currentPage = page) => {
    try {
      setLoading(true);
      const response = await booksAPI.getAllBooks(currentPage, limit, statusFilter);
      const { data, meta } = response.data;
      setBooks(Array.isArray(data) ? data : []);
      if (meta) {
        setTotalPages(meta.pages || 1);
        setTotalItems(meta.total || 0);
      }
    } catch (error) {
      console.error('Error fetching books:', error);
      setSnackbar({ open: true, message: 'Failed to fetch books', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await categoriesAPI.getAllCategories();
      setCategories(response.data?.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  useEffect(() => {
    fetchBooks(page);
    fetchCategories();
  }, [page, limit, statusFilter]);

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(1);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingBook(null);
  };

  const handleOpen = (book = null) => {
    setEditingBook(book);
    setOpen(true);
  };

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    try {
      if (editingBook) {
        await booksAPI.updateBook(editingBook.id, values);
        setSnackbar({ open: true, message: 'Book updated successfully', severity: 'success' });
      } else {
        await booksAPI.createBook(values);
        setSnackbar({ open: true, message: 'Book created successfully', severity: 'success' });
      }
      resetForm();
      handleClose();
      fetchBooks();
    } catch (error) {
      console.error('Error saving book:', error);
      setSnackbar({ open: true, message: 'Failed to save book', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteBook = async (book) => {
    try {
      await booksAPI.deleteBook(book.id);
      setSnackbar({ open: true, message: 'Book deleted successfully', severity: 'success' });
      fetchBooks();
    } catch (error) {
      console.error('Error deleting book:', error);
      setSnackbar({ open: true, message: 'Failed to delete book', severity: 'error' });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ open: false, message: '', severity: 'success' });
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleBookClick = (book) => {
    navigate(`/books/${book.id}?fromPage=${page}&limit=${limit}`);
  };

  const handleIsbnSuccess = (data, wasPublished) => {
    setSnackbar({
      open: true,
      message: `Book "${data.title}" ${wasPublished ? 'created and published' : 'created'} successfully`,
      severity: 'success'
    });
    fetchBooks();
  };

  const handleBatchIsbnSuccess = (data) => {
    setSnackbar({
      open: true,
      message: data.message || `Batch job started with ${data.isbnCount} ISBNs`,
      severity: 'success'
    });
  };

  const handleTogglePublishStatus = async (book) => {
    try {
      const newStatus = book.status === 'published' ? 'draft' : 'published';
      await booksAPI.patchBook(book.id, { status: newStatus });
      setBooks(books.map(b => 
        b.id === book.id 
          ? { ...b, status: newStatus } 
          : b
      ));
      setSnackbar({ 
        open: true, 
        message: `Book ${newStatus === 'published' ? 'published' : 'unpublished'} successfully`, 
        severity: 'success' 
      });
    } catch (error) {
      console.error('Error toggling book status:', error);
      setSnackbar({ 
        open: true, 
        message: 'Failed to update book status', 
        severity: 'error' 
      });
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    setLimit(newPageSize);
    setPage(1);
  };

  const handleGoogleBookAdd = (bookData) => {
    setSnackbar({
      open: true,
      message: `Book "${bookData.title}" added successfully`,
      severity: 'success'
    });
    fetchBooks();
  };

  return (
    <Box sx={{ 
      maxWidth: '100%', 
      overflowX: 'hidden'
    }}>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
        Books Management
      </Typography>
      
      <Box sx={{ 
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: 'center',
        gap: 2,
        mb: 3
      }}>
        <FormControl 
          variant="outlined" 
          size="small"
          sx={{ 
            minWidth: 200,
            width: { xs: '100%', sm: 'auto' }
          }}
        >
          <InputLabel id="status-filter-label">
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FilterListIcon fontSize="small" sx={{ mr: 0.5 }} />
              Status Filter
            </Box>
          </InputLabel>
          <Select
            labelId="status-filter-label"
            value={statusFilter}
            onChange={handleStatusFilterChange}
            label="Status Filter"
          >
            <MenuItem value="">All Statuses</MenuItem>
            <MenuItem value="published">Published</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="archived">Archived</MenuItem>
          </Select>
        </FormControl>
        
        <Box sx={{ flexGrow: 1 }} /> 
        
        <Typography variant="body2" color="text.secondary">
          {totalItems} {totalItems === 1 ? 'book' : 'books'} found
        </Typography>
      </Box>
      
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2, 
        mb: 3,
        '& .MuiButton-root': {
          width: { xs: '100%', sm: 'auto' }
        }
      }}>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => handleOpen()}>
          Add Book Manually
        </Button>
        <Button 
          variant="outlined" 
          color="primary" 
          startIcon={<QrCodeScannerIcon />} 
          onClick={() => setIsbnDialogOpen(true)}
        >
          Add by ISBN
        </Button>
        <Button 
          variant="outlined" 
          color="secondary" 
          startIcon={<LibraryBooksIcon />} 
          onClick={() => setBatchIsbnDialogOpen(true)}
        >
          Batch ISBN Import
        </Button>
        <Button 
          variant="outlined" 
          color="info" 
          startIcon={<SearchIcon />} 
          onClick={() => setGoogleBookSearchOpen(true)}
        >
          Search Google Books
        </Button>
      </Box>

      <Paper elevation={1} sx={{ 
        overflowX: 'auto',
        maxWidth: '100%',
        width: '100%'
      }}>
        <BooksList
          books={books}
          loading={loading}
          totalItems={totalItems}
          page={page}
          limit={limit}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onBookClick={handleBookClick}
          onTogglePublishStatus={handleTogglePublishStatus}
          onDeleteBook={handleDeleteBook}
          isMobile={isMobile}
        />
      </Paper>

      <Dialog 
        open={open} 
        onClose={handleClose}
        fullScreen={isMobile}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{editingBook ? 'Edit Book' : 'Add Book'}</DialogTitle>
        <DialogContent>
          <Formik
            initialValues={{
              title: editingBook?.title || '',
              author: editingBook?.author || '',
              durationOptions: editingBook?.durationOptions || [],
              cover: editingBook?.cover || '',
              status: editingBook?.status || '',
              audioUrls: editingBook?.audioUrls || {},
              categoryIds: editingBook?.categoryIds || []
            }}
            validationSchema={BookSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched, isSubmitting }) => (
              <Form>
                <Field
                  as={TextField}
                  name="title"
                  label="Title"
                  fullWidth
                  margin="normal"
                  error={touched.title && !!errors.title}
                  helperText={touched.title && errors.title}
                />
                <Field
                  as={TextField}
                  name="author"
                  label="Author"
                  fullWidth
                  margin="normal"
                  error={touched.author && !!errors.author}
                  helperText={touched.author && errors.author}
                />
                <Field
                  as={TextField}
                  name="cover"
                  label="Cover URL"
                  fullWidth
                  margin="normal"
                  error={touched.cover && !!errors.cover}
                  helperText={touched.cover && errors.cover}
                />
                <FormControl fullWidth margin="normal">
                  <InputLabel>Status</InputLabel>
                  <Field
                    as={Select}
                    name="status"
                    error={touched.status && !!errors.status}
                  >
                    <MenuItem value="Draft§">Draft</MenuItem>
                    <MenuItem value="Published">Published</MenuItem>
                  </Field>
                  {touched.status && errors.status && (
                    <Typography color="error" variant="caption">
                      {errors.status}
                    </Typography>
                  )}
                </FormControl>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Categories</InputLabel>
                  <Field
                    as={Select}
                    name="categoryIds"
                    multiple
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                        {selected.map((value) => (
                          <Chip key={value} label={categories.find((cat) => cat.id === value)?.name} />
                        ))}
                      </Box>
                    )}
                  >
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Field>
                  {touched.categoryIds && errors.categoryIds && (
                    <Typography color="error" variant="caption">
                      {errors.categoryIds}
                    </Typography>
                  )}
                </FormControl>
                <DialogActions sx={{ px: isMobile ? 0 : 2, pt: 2, pb: isMobile ? 4 : 2 }}>
                  <Button onClick={handleClose} color="primary">
                    Cancel
                  </Button>
                  <Button type="submit" color="primary" disabled={isSubmitting}>
                    {editingBook ? 'Update' : 'Add'}
                  </Button>
                </DialogActions>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
      
      <GoogleBookSearch
        open={googleBookSearchOpen}
        onClose={() => setGoogleBookSearchOpen(false)}
        onBookAdd={handleGoogleBookAdd}
        fullScreen={isMobile}
      />
      
      <IsbnBookDialog
        open={isbnDialogOpen}
        onClose={() => setIsbnDialogOpen(false)}
        onSuccess={handleIsbnSuccess}
        fullScreen={isMobile}
      />
      
      <BatchIsbnDialog
        open={batchIsbnDialogOpen}
        onClose={() => setBatchIsbnDialogOpen(false)}
        onSuccess={handleBatchIsbnSuccess}
        fullScreen={isMobile}
      />
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
